import { Activity, BookOpen, Boxes, BrainCircuit, Clock3, Cpu, Database, FileKey2, HardDriveUpload, ListChecks, LogOut, Menu, PanelLeftClose, PanelLeftOpen, ReceiptText, Server, Workflow } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import type { Session } from "../types";

type NavigationItem = readonly [path: string, label: string, icon: LucideIcon];

const overview: NavigationItem = ["/", "Overview", Activity];
const navigationGroups: ReadonlyArray<{ label: string; items: readonly NavigationItem[] }> = [
  {
    label: "Show Generator",
    items: [
      ["/show-generator/stories", "Stories", BookOpen],
      ["/show-generator/operations", "Story Operations", Workflow],
    ],
  },
  {
    label: "AI Router",
    items: [
      ["/models", "Models", Boxes],
      ["/tasks", "Router Tasks", BrainCircuit],
    ],
  },
  {
    label: "ComfyUI Server Manager",
    items: [
      ["/servers", "Servers", Server],
      ["/rental-orders", "Rental Orders", ReceiptText],
      ["/operations", "Operations", ListChecks],
      ["/server-jobs", "Server Jobs", Cpu],
      ["/uploaded-inputs", "Uploaded Inputs", HardDriveUpload],
      ["/refresh-events", "Refresh Events", Clock3],
      ["/idempotency-keys", "Idempotency Keys", FileKey2],
      ["/prompt-outcomes", "Prompt Outcomes", Database],
    ],
  },
];
const navigation: NavigationItem[] = [overview, ...navigationGroups.flatMap((group) => group.items)];

export function AppLayout({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const current = navigation.find(([path]) => location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`)))?.[1] ?? "Operations";
  return <div className={`app-shell${collapsed ? " sidebar-collapsed" : ""}`}>
    <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
      <header className="sidebar-brand"><div className="brand-mark small"><span>SH</span></div><div className="sidebar-brand-copy"><strong>Sidehop</strong><span>Operations</span></div><button className="icon-button sidebar-toggle desktop-only" type="button" title={collapsed ? "Show sidebar" : "Hide sidebar"} aria-label={collapsed ? "Show sidebar" : "Collapse sidebar"} onClick={() => setCollapsed((current) => !current)}>{collapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}</button><button className="icon-button sidebar-toggle mobile-only" type="button" title="Hide sidebar" aria-label="Hide sidebar" onClick={() => setOpen(false)}><PanelLeftClose size={19} /></button></header>
      <nav>
        <NavItem item={overview} onSelect={() => setOpen(false)} />
        {navigationGroups.map((group) => <section className="nav-group" key={group.label} aria-label={group.label}>
          <div className="nav-group-label">{group.label}</div>
          {group.items.map((item) => <NavItem item={item} key={item[0]} onSelect={() => setOpen(false)} />)}
        </section>)}
      </nav>
      <footer><div className="user-chip"><span>{session.email.slice(0, 1).toUpperCase()}</span><div><strong>{session.email}</strong><small>Administrator</small></div></div><button className="icon-button" type="button" onClick={onLogout} title="Sign out"><LogOut size={18} /></button></footer>
    </aside>
    {open ? <button className="sidebar-scrim" aria-label="Close navigation" onClick={() => setOpen(false)} /> : null}
    <div className="workspace"><header className="mobile-header"><button className="icon-button" type="button" title="Open sidebar" aria-label="Open sidebar" onClick={() => setOpen(true)}><Menu size={20} /></button><strong>{current}</strong></header><main className="content"><Outlet /></main></div>
  </div>;
}

function NavItem({ item: [path, label, Icon], onSelect }: { item: NavigationItem; onSelect: () => void }) {
  return <NavLink to={path} end={path === "/"} onClick={onSelect} title={label}><Icon size={18} /><span>{label}</span></NavLink>;
}
